import { useAppDispatch, useAppSelector } from '@/shared/hooks';
import { useErrorHandler } from '@/shared/hooks/useErrorHandler';
import { useSuccessMessage } from '@/shared/hooks/useSuccessMessage';
import type { EntityFilters } from '@/shared/types/entity';
import { unwrapDispatchResult } from '@/shared/utils/redux';
import type { RootState } from '@/store';
import { store } from '@/store';
import type { ActionCreatorWithPayload } from '@reduxjs/toolkit';
import { useCallback, useEffect } from 'react';

export interface UseEntityListConfig<T, F extends EntityFilters> {
  selector: (state: RootState) => {
    items: T[];
    loading: boolean;
    error: string | { status: number; message?: string } | null;
    currentPage: number;
    perPage: number;
    total: number;
    lastPage: number;
    filters: F;
  };
  fetchAction: () => unknown;
  deleteAction: (id: number) => unknown;
  setPageAction: ActionCreatorWithPayload<number>;
  setPerPageAction: ActionCreatorWithPayload<number>;
  setFiltersAction: ActionCreatorWithPayload<Partial<F>>;
  defaultFilters: F;
  entityName: string;
}

export const useEntityList = <T, F extends EntityFilters>({
  selector,
  fetchAction,
  deleteAction,
  setPageAction,
  setPerPageAction,
  setFiltersAction,
  defaultFilters,
  entityName,
}: UseEntityListConfig<T, F>) => {
  const dispatch = useAppDispatch();
  const { items, loading, error, currentPage, perPage, total, lastPage, filters } =
    useAppSelector(selector);

  const { handleError, clearError } = useErrorHandler({
    defaultMessage: 'Failed to process items',
    onError: (err) => {
      console.error('Entity list error:', err);
    },
  });

  const { showSuccess } = useSuccessMessage();
  const dispatchAndUnwrap = useCallback(
    <R>(action: unknown): Promise<R> => {
      return unwrapDispatchResult<R>(dispatch(action as never));
    },
    [dispatch],
  );

  useEffect(() => {
    if (
      typeof currentPage === 'number' &&
      currentPage > 0 &&
      typeof perPage === 'number' &&
      perPage > 0
    ) {
      void Promise.resolve()
        .then(() => dispatchAndUnwrap(fetchAction()))
        .then(clearError)
        .catch(handleError);
    }
  }, [currentPage, perPage, filters, fetchAction, dispatchAndUnwrap, clearError, handleError]);

  const handleDelete = useCallback(
    (id: number) => {
      if (window.confirm('Are you sure you want to delete this item?')) {
        const deleteData = async () => {
          try {
            const pageBefore = selector(store.getState()).currentPage;
            await dispatchAndUnwrap(deleteAction(id));
            const pageAfter = selector(store.getState()).currentPage;
            if (pageAfter === pageBefore) {
              await dispatchAndUnwrap(fetchAction());
            }
            clearError();
            showSuccess(`${entityName} deleted successfully!`);
          } catch (err) {
            handleError(err);
          }
        };

        void deleteData();
      }
    },
    [
      deleteAction,
      fetchAction,
      selector,
      clearError,
      handleError,
      showSuccess,
      entityName,
      dispatchAndUnwrap,
    ],
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      dispatch(setPageAction(newPage));
    },
    [dispatch, setPageAction],
  );

  const handlePerPageChange = useCallback(
    (newPerPage: number) => {
      dispatch(setPerPageAction(newPerPage));
    },
    [dispatch, setPerPageAction],
  );

  const handleFiltersChange = useCallback(
    (newFilters: Partial<F>) => {
      dispatch(setFiltersAction(newFilters));
    },
    [dispatch, setFiltersAction],
  );

  const handleClearFilters = useCallback(() => {
    dispatch(setFiltersAction(defaultFilters));
  }, [dispatch, setFiltersAction, defaultFilters]);

  return {
    items,
    loading,
    error,
    currentPage,
    perPage,
    total,
    lastPage,
    filters,
    handleDelete,
    handlePageChange,
    handlePerPageChange,
    handleFiltersChange,
    handleClearFilters,
  };
};
